import { reactive, ref, shallowReadonly } from 'vue';
import { defineStore } from '@dh-io-owpi/shared/src/stores/store';
import type { BrandId, Paint, Style } from '@dh-io-owpi/backend-api/src/data-contracts';
import { useAemData } from '@/composables/useAemData';

/**
 * This store is used to store the state of the page that affects the URL.
 * It will allow us to deep-link to a specific sub-brand
 * Later it may be extended to paint / style.
 */
export const usePageStateStore = defineStore('spa-pageState', () => {
  const brandId = ref(splitPathname(location.pathname)[0]);
  const style = ref<Style>();
  const paint = ref<Paint>();
  const aemData = useAemData();

  function setPaint(newPaint: Paint) {
    paint.value = newPaint;
  }

  function setStyle(newStyle: Style) {
    if (style.value?.id === newStyle.id) return;
    style.value = newStyle;

    // if currently selected paint doesn't exist for new selected style, reset to first paint
    setPaint(newStyle.paints.find((p) => p.id === paint.value?.id) ?? newStyle.paints[0]);

    setBrandId(newStyle.brandId as BrandId);
  }

  function setBrandId(newBrandId: BrandId) {
    brandId.value = newBrandId;
    // update the URL only if not in AEM edit mode (It interferes with some AEM author functionalities)
    if (!aemData.isAemEditMode) updateUrlWithBrandId(newBrandId);
  }

  function setStyles(styles: Style[]) {
    setStyle(styles.find((s) => s.brandId === brandId.value) ?? styles[0]);
  }

  // make the state readonly to prevent accidental changes from outside the store
  return shallowReadonly(
    reactive({
      brandId,
      style,
      paint,
      setPaint,
      setStyle,
      setStyles,
    }),
  );
});

const pathToBrandIdMap: Record<string, BrandId> = {
  '/mercedes-benz': 'MERCEDES_BENZ',
  '/amg': 'MERCEDES_AMG',
  '/maybach': 'MERCEDES_MAYBACH',
};

const brandIdToPathMap: Record<BrandId, string> = {
  MERCEDES_BENZ: '/mercedes-benz',
  MERCEDES_AMG: '/amg',
  MERCEDES_MAYBACH: '/maybach',
};

/**
 * Splits the pathname into brandId and path.
 * Handles the following cases:
 * /<something>,    /a-class.html/<something>     -> MERCEDES_BENZ
 * /,               /a-class.html                 -> MERCEDES_BENZ
 * /mercedes-benz,  /a-class.html/mercedes-benz   -> MERCEDES_BENZ
 * /amg,            /a-class.html/amg             -> MERCEDES_AMG
 * /maybach,        /a-class.html/maybach         -> MERCEDES_MAYBACH
 * If the pathname does not have a valid brandId, it will return MERCEDES_BENZ as brandId.
 */
function splitPathname(pathname: string): [brandId: BrandId, path: string] {
  const splitHtml = pathname.split('.html');
  if (splitHtml.length > 1) {
    return [pathToBrandIdMap[splitHtml[1] ?? ''] ?? 'MERCEDES_BENZ', splitHtml[0] + '.html'];
  }
  return [pathToBrandIdMap[pathname] ?? 'MERCEDES_BENZ', ''];
}

/**
 * Updates the URL with the given brandId.
 * Handles the following cases:
 * MERCEDES_BENZ    -> /mercedes-benz,  /a-class.html/mercedes-benz
 * MERCEDES_AMG     -> /amg,            /a-class.html/amg
 * MERCEDES_MAYBACH -> /maybach,        /a-class.html/maybach
 * Only handle non .html paths in DEV mode.
 */
function updateUrlWithBrandId(brandId: BrandId) {
  const path = splitPathname(location.pathname)[1];

  if (path || import.meta.env.DEV) {
    const url = new URL(location.href);
    url.pathname = path + brandIdToPathMap[brandId];
    history.replaceState({}, '', url);
  }
}
